import { useEffect, useState, useCallback } from 'react'
import { flushPreviousDay } from './journal'
import { useTodayKey, todayKey } from './dateUtils'

const STORAGE_KEY = 'gps-journal-project-time'
const CHANGE_EVENT = 'gps-project-time-changed'

interface ProjectTimeRecord {
  date: string
  minutesLogged: number
  workedOn: string
}

function loadRaw(): ProjectTimeRecord | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ProjectTimeRecord
  } catch {
    return null
  }
}

function saveRaw(record: ProjectTimeRecord) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
  } catch {
    // ignore
  }
}

function readForToday(today: string): ProjectTimeRecord {
  const record = loadRaw()
  if (record && record.date === today) return record
  return { date: today, minutesLogged: 0, workedOn: '' }
}

/**
 * Shared, reactive store for today's project time logged.
 * Resets at midnight (yesterday's record is flushed to the journal log first).
 */
export function useProjectTime() {
  const today = useTodayKey()

  const [minutesLogged, setMinutesLoggedState] = useState(() => {
    flushPreviousDay()
    return readForToday(todayKey()).minutesLogged
  })
  const [workedOn, setWorkedOnState] = useState(
    () => readForToday(todayKey()).workedOn,
  )

  // On date rollover, flush yesterday and reset to today's empty values
  useEffect(() => {
    flushPreviousDay()
    const fresh = readForToday(today)
    setMinutesLoggedState(fresh.minutesLogged)
    setWorkedOnState(fresh.workedOn)
  }, [today])

  // Listen for cross-component / cross-tab updates
  useEffect(() => {
    const refresh = () => {
      const record = loadRaw()
      if (record && record.date === todayKey()) {
        setMinutesLoggedState(record.minutesLogged)
        setWorkedOnState(record.workedOn)
      } else {
        setMinutesLoggedState(0)
        setWorkedOnState('')
      }
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh()
    }
    window.addEventListener(CHANGE_EVENT, refresh)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const addMinutes = useCallback(
    (delta: number) => {
      setMinutesLoggedState((prev) => {
        const next = Math.max(0, prev + delta)
        saveRaw({ date: todayKey(), minutesLogged: next, workedOn })
        return next
      })
    },
    [workedOn],
  )

  const resetMinutes = useCallback(() => {
    setMinutesLoggedState(0)
    saveRaw({ date: todayKey(), minutesLogged: 0, workedOn })
  }, [workedOn])

  const setWorkedOn = useCallback((value: string) => {
    setWorkedOnState(value)
    setMinutesLoggedState((prev) => {
      saveRaw({ date: todayKey(), minutesLogged: prev, workedOn: value })
      return prev
    })
  }, [])

  return { minutesLogged, workedOn, addMinutes, resetMinutes, setWorkedOn }
}

export function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h 00m`
  return `${h}h ${String(m).padStart(2, '0')}m`
}
